import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Select,
  Button,
  Table,
  Progress,
  Space,
  message,
  Alert,
} from 'antd';
import {
  DollarOutlined,
  RiseOutlined,
  TeamOutlined,
  ShoppingCartOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { MetricCard, LineChart } from '../../components';
import { usePlatformRevenue, useTopTenants, useRevenueByPlan } from '../../hooks/useReports';
import { reportService, ReportPeriod } from '../../services/report.service';
import { formatVND } from '@erpgold/shared';

const { Title, Text } = Typography;
const { Option } = Select;

export const PlatformReports: React.FC = () => {
  const [period, setPeriod] = useState<ReportPeriod>('30d');
  const [exporting, setExporting] = useState(false);

  const { data: revenueData, loading: revenueLoading, error, refetch } = usePlatformRevenue(period);
  const { tenants, loading: tenantsLoading } = useTopTenants(period);
  const { plans, loading: plansLoading } = useRevenueByPlan(period);

  const formatCurrency = (value: number) => formatVND(value);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await reportService.exportReport('revenue', period);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `platform-revenue-${period}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Report exported successfully');
    } catch {
      message.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const tenantColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 60,
      render: (_: unknown, __: unknown, index: number) => index + 1,
    },
    {
      title: 'Tenant',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right' as const,
      render: (v: number) => formatCurrency(v),
    },
    {
      title: '% Share',
      dataIndex: 'share',
      key: 'share',
      align: 'right' as const,
      render: (v: number) => `${v.toFixed(1)}%`,
    },
  ];

  if (error) {
    return (
      <Alert
        message="Lỗi tải dữ liệu"
        description={error.message || 'Vui lòng thử lại sau'}
        type="error"
        showIcon
        action={<Button onClick={refetch}>Thử lại</Button>}
      />
    );
  }

  const chartData = revenueData?.revenueTrend?.map((item) => ({
    date: item.date,
    value: item.value,
  })) || [];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Platform Reports</Title>
        </Col>
        <Col>
          <Space>
            <Select value={period} onChange={setPeriod} style={{ width: 120 }}>
              <Option value="7d">Last 7 days</Option>
              <Option value="30d">Last 30 days</Option>
              <Option value="90d">Last 90 days</Option>
              <Option value="365d">Last 365 days</Option>
            </Select>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
            >
              Export CSV
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <MetricCard
            title="Total Revenue"
            value={revenueData?.totalRevenue || 0}
            prefix="₫"
            precision={0}
            icon={<DollarOutlined />}
            color="#52c41a"
            loading={revenueLoading}
          />
        </Col>
        <Col span={6}>
          <MetricCard
            title="MRR"
            value={revenueData?.mrr || 0}
            prefix="₫"
            precision={0}
            icon={<RiseOutlined />}
            color="#1890ff"
            loading={revenueLoading}
          />
        </Col>
        <Col span={6}>
          <MetricCard
            title="Active Tenants"
            value={revenueData?.activeTenants || 0}
            icon={<TeamOutlined />}
            color="#722ed1"
            loading={revenueLoading}
          />
        </Col>
        <Col span={6}>
          <MetricCard
            title="Avg Order Value"
            value={revenueData?.avgOrderValue || 0}
            prefix="₫"
            precision={0}
            icon={<ShoppingCartOutlined />}
            color="#fa8c16"
            loading={revenueLoading}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <LineChart
            title="Revenue Trend"
            data={chartData}
            loading={revenueLoading}
            yAxis={{
              title: 'Revenue (₫)',
              formatter: (v: number) => `₫${(v / 1000000).toFixed(0)}M`,
            }}
            tooltip={{
              formatter: (datum: { value: number }) => ({
                name: 'Revenue',
                value: formatCurrency(datum.value),
              }),
            }}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={14}>
          <Card title="Revenue by Tenant (Top 10)">
            <Table
              columns={tenantColumns}
              dataSource={tenants}
              loading={tenantsLoading}
              rowKey="tenantId"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col span={10}>
          <Card title="Revenue by Plan">
            <Space direction="vertical" style={{ width: '100%' }}>
              {plansLoading ? (
                <Text type="secondary">Loading...</Text>
              ) : plans.length > 0 ? (
                plans.map((item) => (
                  <div key={item.plan}>
                    <Row justify="space-between" style={{ marginBottom: 4 }}>
                      <Col>
                        <Text strong>{item.plan.toUpperCase()}</Text>
                      </Col>
                      <Col>
                        <Text>{formatCurrency(item.revenue)}</Text>
                      </Col>
                    </Row>
                    <Progress
                      percent={item.percentage}
                      size="small"
                      strokeColor={{
                        '0%': '#108ee9',
                        '100%': '#87d068',
                      }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.percentage.toFixed(1)}% of total
                    </Text>
                  </div>
                ))
              ) : (
                <Text type="secondary">No data available</Text>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PlatformReports;
